import { Injectable, computed, signal } from '@angular/core'
import { AuthState, UserInfo, TokenStorage, AuthSsoUser } from '@shared/models/auth/auth.models'

/**
 * Authentication Store
 *
 * Manages authentication state using Angular signals for reactive state management.
 * Provides a centralized store for authentication data, user information, and auth status.
 *
 * Features:
 * - Reactive state management with signals
 * - Token persistence and retrieval
 * - User session management
 * - Error state handling
 * - Computed properties for derived state
 */
@Injectable({ providedIn: 'root' })
export class AuthStore {
  // Private signals for internal state management
  private readonly _isAuthenticated = signal<boolean>(false)
  private readonly _isSsoAuthenticated = signal<boolean>(false)
  private readonly _user = signal<UserInfo | null>(null)
  private readonly _ssoUser = signal<AuthSsoUser | null>(null)
  private readonly _token = signal<string | null>(null)
  private readonly _refreshToken = signal<string | null>(null)
  private readonly _refreshTokenExpiresAt = signal<Date | null>(null)
  private readonly _ssoRefreshToken = signal<string | null>(null)
  private readonly _ssoRefreshExpires = signal<Date | null>(null)
  private readonly _isLoading = signal<boolean>(false)
  private readonly _error = signal<string | null>(null)

  // Read-only public signals
  readonly isAuthenticated = this._isAuthenticated.asReadonly()
  readonly isSsoAuthenticated = this._isSsoAuthenticated.asReadonly()
  readonly user = this._user.asReadonly()
  readonly ssoUser = this._ssoUser.asReadonly()
  readonly token = this._token.asReadonly()
  readonly refreshToken = this._refreshToken.asReadonly()
  readonly refreshTokenExpiresAt = this._refreshTokenExpiresAt.asReadonly()
  readonly ssoRefreshToken = this._ssoRefreshToken.asReadonly()
  readonly ssoRefreshExpires = this._ssoRefreshExpires.asReadonly()
  readonly isLoading = this._isLoading.asReadonly()
  readonly error = this._error.asReadonly()

  // Computed properties for derived state
  readonly isTokenExpired = computed(() => {
    const user = this._user()
    if (!user) return true
    return Date.now() >= user.exp * 1000
  })

  readonly isRefreshTokenExpired = computed(() => {
    const expiresAt = this._refreshTokenExpiresAt()
    if (!expiresAt) return true
    return Date.now() >= expiresAt.getTime()
  })

  readonly canRefreshToken = computed(() => {
    return !this.isRefreshTokenExpired() && !!this._refreshToken()
  })

  readonly isSsoRefreshTokenExpired = computed(() => {
    const expiresAt = this._ssoRefreshExpires()
    if (!expiresAt) return true
    return Date.now() >= expiresAt.getTime()
  })

  readonly canRefreshSsoToken = computed(() => {
    return !this.isSsoRefreshTokenExpired() && !!this._ssoRefreshToken()
  })

  /** Role for both auth types */
  readonly userRole = computed(() => {
    return this._user()?.role || this._ssoUser()?.roleClesame?.code || null
  })

  /** Numeric userId for regular users (from JWT). Null for SSO users. */
  readonly userId = computed(() => {
    return this._user()?.userId || this._ssoUser()?.id || null
  })

  /**
   * Unified string identifier that works for both auth types.
   * Regular login → String(userId), SSO login → ssoUser.id
   */
  readonly userIdentifier = computed((): string | null => {
    const userId = this._user()?.userId
    if (userId) return String(userId)
    return this._ssoUser()?.id ?? null
  })

  /**
   * Sets the loading state
   * @param loading Whether authentication is in progress
   */
  setLoading(loading: boolean): void {
    this._isLoading.set(loading)
  }

  /**
   * Sets the authentication error
   * @param error Error message or null to clear
   */
  setError(error: string | null): void {
    this._error.set(error)
  }

  /**
   * Sets the complete authentication state
   * @param user User information
   * @param token Access token
   * @param refreshToken Refresh token
   * @param refreshTokenExpiresAt Refresh token expiration date
   */
  setAuthState(user: UserInfo, token: string, refreshToken: string, refreshTokenExpiresAt: Date): void {
    this._user.set(user)
    this._token.set(token)
    this._refreshToken.set(refreshToken)
    this._refreshTokenExpiresAt.set(refreshTokenExpiresAt)
    this._isAuthenticated.set(true)
    this._error.set(null)
  }

  setSsoAuthState(user: AuthSsoUser, token: string, refreshToken: string, refreshExpires: string): void {
    this._ssoUser.set(user)
    this._token.set(token)
    this._ssoRefreshToken.set(refreshToken)
    this._ssoRefreshExpires.set(new Date(refreshExpires))
    this._isAuthenticated.set(true)
    this._isSsoAuthenticated.set(true)
    this._error.set(null)
  }

  /**
   * Updates only the access token
   * @param token New access token
   * @param user Updated user information (optional)
   */
  updateToken(token: string, user?: UserInfo): void {
    this._token.set(token)
    if (user) {
      this._user.set(user)
    }
  }

  /**
   * Clears all authentication state (logout)
   */
  clearAuthState(): void {
    this._isAuthenticated.set(false)
    this._isSsoAuthenticated.set(false)
    this._user.set(null)
    this._ssoUser.set(null)
    this._token.set(null)
    this._refreshToken.set(null)
    this._refreshTokenExpiresAt.set(null)
    this._ssoRefreshToken.set(null)
    this._ssoRefreshExpires.set(null)
    this._error.set(null)
  }

  /**
   * Gets the current authentication state
   * @returns Current authentication state
   */
  getAuthState(): AuthState {
    return {
      isAuthenticated: this._isAuthenticated(),
      user: this._user(),
      token: this._token(),
      refreshToken: this._refreshToken(),
      refreshTokenExpiresAt: this._refreshTokenExpiresAt(),
      isLoading: this._isLoading(),
      error: this._error(),
    }
  }

  /**
   * Gets token storage data for persistence
   * @returns Token storage data or null if not authenticated
   */
  getTokenStorage(): TokenStorage | null {
    const user = this._user()
    const token = this._token()
    const refreshToken = this._refreshToken()
    const refreshTokenExpiresAt = this._refreshTokenExpiresAt()

    if (!user || !token || !refreshToken || !refreshTokenExpiresAt) {
      return null
    }

    return {
      token,
      refreshToken,
      refreshTokenExpiresAt: refreshTokenExpiresAt.toISOString(),
      userId: user.userId,
      role: user.role,
    }
  }

  /**
   * Restores authentication state from storage
   * @param storage Token storage data
   * @param user User information
   */
  restoreFromStorage(storage: TokenStorage, user: UserInfo): void {
    this._token.set(storage.token)
    this._refreshToken.set(storage.refreshToken)
    this._refreshTokenExpiresAt.set(new Date(storage.refreshTokenExpiresAt))
    this._user.set(user)
    this._isAuthenticated.set(true)
    this._error.set(null)
  }

  restoreSsoFromStorage(
    token: string,
    user: AuthSsoUser,
    refreshToken?: string | null,
    refreshExpires?: string | null
  ): void {
    this._token.set(token)
    this._ssoUser.set(user)
    this._ssoRefreshToken.set(refreshToken ?? null)
    this._ssoRefreshExpires.set(refreshExpires ? new Date(refreshExpires) : null)
    this._isAuthenticated.set(true)
    this._isSsoAuthenticated.set(true)
    this._error.set(null)
  }

  /**
   * Checks if the current user has a specific role
   * @param role Role to check
   * @returns Whether user has the specified role
   */
  hasRole(role: string): boolean {
    const user = this._user()
    if (user) return user.role === role
    return this._ssoUser()?.roleClesame?.code === role
  }

  hasAnyRole(roles: string[]): boolean {
    const user = this._user()
    if (user) return roles.includes(user.role)
    const ssoUser = this._ssoUser()
    return ssoUser ? roles.includes(ssoUser.roleClesame?.code) : false
  }
}
